<script setup lang="ts">
import { Search } from '@element-plus/icons-vue';
import {
  ref, watch, onUnmounted,
} from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  sortValue: {
    type: Object,
    default: () => ({}),
  },
  sortOptions: {
    type: Array,
    default: () => [],
  },
  isFiltered: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'update:sortValue', 'search', 'sort', 'clear']);

const search = ref(props.modelValue);
const sort = ref(props.sortValue);

// Debounce timeout reference for cleanup
let debounceTimeout: ReturnType<typeof setTimeout> | null = null;

// Watch for search input changes with manual debounce
watch(
  search,
  (newValue) => {
    // Clear any pending timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    
    // Update v-model immediately for UI responsiveness
    emit('update:modelValue', newValue);
    
    // Debounce the actual search
    debounceTimeout = setTimeout(() => {
      emit('search', newValue);
      debounceTimeout = null;
    }, 300);
  },
);

watch(() => props.modelValue, (newValue) => {
  // Only update if different to avoid infinite loops
  if (search.value !== newValue) {
    search.value = newValue;
  }
});

watch(() => props.sortValue, (newValue) => {
  sort.value = newValue;
});

const handleSortChange = (value) => {
  sort.value = value;
  emit('update:sortValue', value);
  emit('sort', value);
};

const handleClear = () => {
  search.value = '';
  emit('clear');
};

// Cleanup on unmount
onUnmounted(() => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
    debounceTimeout = null;
  }
});

</script>

<template lang="pug">
.c-request-filters
  .c-request-filters__search
    el-input(
      v-model="search"
      :prefix-icon="Search"
      type="text"
      placeholder="Search requests by name, email, or page..."
      clearable
      size="large"
    )

  .c-request-filters__actions
    el-select.c-request-filters__sort(
      v-model="sort"
      placeholder="Sort by"
      size="large"
      @change="handleSortChange"
    )
      el-option(
        v-for="item in sortOptions"
        :key="item.value"
        :label="item.label"
        :value="item"
      )
</template>

<style lang="scss" scoped>
// Color Variables - Match Dashboard.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$color-purple-text: #4349dd;

.c-request-filters {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 0;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }

  &__search {
    flex: 1;
    min-width: 0;

    :deep(.el-input) {
      .el-input__wrapper {
        border-radius: 10px;
        border: 1.5px solid $gray-200;
        background: #F0F0F3;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
        padding: 0 12px;
        height: 40px;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
          border-color: $color-purple-text;
          box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
        }

        &.is-focus {
          border-color: $color-purple-text;
          box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
        }
      }

      .el-input__inner {
        height: 100%;
        font-size: 0.875rem;
        color: $gray-700;
        background: transparent;
        
        &::placeholder {
          color: $gray-400;
          font-weight: 400;
        }
      }

      .el-input__prefix {
        color: $gray-400;
        font-size: 1.125rem;
        
        .el-icon {
          font-size: 1.125rem;
        }
      }

      .el-input__suffix {
        .el-input__clear {
          color: $gray-400;
          transition: color 0.2s ease;
          
          &:hover {
            color: $color-purple-text;
          }
        }
      }
    }
  }

  &__actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;

    @media (max-width: 767px) {
      width: 100%;
    }
  }

  &__sort {
    width: 240px;

    @media (max-width: 767px) {
      width: 100%;
    }

    :deep(.el-select__wrapper) {
      border-radius: 10px;
      border: 1.5px solid $gray-200;
      background: #F0F0F3;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
      padding: 0 12px;
      height: 40px;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover {
        border-color: $color-purple-text;
        box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
      }

      &.is-focused {
        border-color: $color-purple-text;
        box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
      }

      .el-select__selected-item {
        font-size: 0.875rem;
        color: $gray-700;
        font-weight: 500;
      }

      .el-select__placeholder {
        color: $gray-400;
        font-size: 0.875rem;
        font-weight: 400;
      }

      .el-select__suffix {
        .el-icon {
          color: $gray-400;
          font-size: 1rem;
          transition: transform 0.2s ease, color 0.2s ease;
        }
      }

      &.is-focused .el-select__suffix .el-icon {
        color: $color-purple-text;
      }
    }
  }
}

// Select Dropdown Styling - Enhanced Modern Design
:deep(.el-select-dropdown) {
  border-radius: 12px;
  border: 1px solid $gray-200;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
  padding: 6px;
  margin-top: 6px;
}

:deep(.el-select-dropdown__item) {
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 2px;
  font-size: 0.9375rem;
  color: $gray-700;
  font-weight: 500;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:last-child {
    margin-bottom: 0;
  }

  &.is-hovering {
    background-color: rgba(1, 190, 176, 0.08);
    color: $color-teal-accent;
    transform: translateX(2px);
  }

  &.is-selected {
    background-color: rgba(1, 190, 176, 0.12);
    color: $color-teal-accent;
    font-weight: 600;
    position: relative;

    &::after {
      content: '✓';
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 1rem;
      font-weight: 700;
    }
  }

  &.is-disabled {
    color: $gray-300;
    cursor: not-allowed;
    
    &:hover {
      background-color: transparent;
      transform: none;
    }
  }
}
</style>
